import React from 'react';
import {
  string,
  bool,
  number,
  func,
  node,
  oneOfType,
  element,
  oneOf
} from 'prop-types';
import uniqueId from 'lodash.uniqueid';
import { themes, ThemeContext, getTheme } from '@ids/theme';
import InlineValidationMessage, {
  getAriaAttributes
} from '@ids/inline-validation-message';
import { styleOverrideReason } from '@ids/core';
import {
  HelperTextWrapper,
  GhostTextWrapper,
  LabelWrapper,
  TextFieldWrapper,
  InputWrapper,
  Input
} from './StyledTextField';

// Do not extend with PureComponent, formatting will break otherwise at the spaces/additional chars of format.
/** TextField component */
export default class TextField extends React.Component {
  static contextType = ThemeContext;

  static propTypes = {
    /**
     * Id for component.
     */
    id: string,
    /**
     * Set input type (text, tel, number etc)
     */
    type: string,
    /**
     * Name to be assigned to the input field.
     */
    name: string,
    /**
     * Disabled prop to pass on to root when set as true.
     */
    // eslint-disable-next-line react/boolean-prop-naming
    disabled: bool,
    /**
     * Error message to be rendered.
     */
    errorText: string,
    /**
     * Value to be displayed in the input field.
     */
    value: oneOfType([number, string]),
    /**
     * warning message to be rendered.
     */
    warningText: string,
    /**
     * warning message to be rendered.
     */
    // eslint-disable-next-line react/boolean-prop-naming
    validationError: bool,
    /**
     * Text to be display for extra information
     */
    helperText: string,
    /**
     * Prop to set desired Icon on textfield. This Icon is from Icons-v3 component.
     * Deprecated. Please use addOnBefore or addOnAfter
     */
    icon: element,
    /**
     * Position of the icon
     * Deprecated. Please use addOnBefore or addOnAfter
     */
    iconPosition: oneOf(['right', 'left']),
    /**
     * Label to be displayed for the field.
     */
    label: string,
    /**
     * Prop for handling callBack for onBlur event.
     */
    onBlur: func,
    /**
     * When there is an application level theme defined, use this prop to override
     * (e.g. 'mint', 'proconnect', 'quickbooks', 'turbotax')
     */
    theme: oneOf(themes),
    /**
     * Prop to set desired Icon/ addonBefore on textfield.
     */
    addonBefore: node,
    /**
     * Prop to set desired Icon/ addonAfter on textfield.
     */
    addonAfter: node,
    /**
     * Width prop to set a custom width needed for textfield is some use cases.
     */
    width: string,
    /**
     * Callback function for change event.
     */
    onChange: func,
    /**
     * The aria-label property surfaces a hidden label to assistive technology. This is critical when the label property is not used.
     */
    'aria-label': string,
    /**
     * A ghost text like placeholder, but can be controllable.
     */
    ghostText: string,
    /**
     * Get reference to input field.
     */
    assignInputRef: func,
    /**
     * Get reference to the label element
     */
    assignLabelRef: func,
    /**
     * AutomationID prop to be used for testing.
     */
    automationId: string,
    /**
     * When `style` or `className` prop is provided, provide the reason behind the override.
     */
    styleOverrideReason
  };

  static defaultProps = {
    id: undefined,
    type: 'text',
    name: null,
    disabled: false,
    icon: null,
    iconPosition: 'right',
    onBlur() {},
    addonBefore: null,
    addonAfter: null,
    width: undefined,
    onChange() {},
    'aria-label': null,
    label: '',
    value: undefined,
    helperText: '',
    errorText: '',
    validationError: false,
    theme: undefined,
    warningText: '',
    ghostText: undefined,
    assignInputRef() {},
    assignLabelRef() {},
    automationId: undefined,
    styleOverrideReason: undefined
  };

  constructor(props) {
    super(props);

    const prefix = 'idsTxtField';
    this.helperTextId = uniqueId(prefix);
    this.errorWarningTextId = uniqueId(prefix);
    this.ariaId = uniqueId(prefix);
  }

  getInputRef = c => {
    this.inputRef = c;
    this.props.assignInputRef(c);
  };

  getLabelRef = c => {
    this.labelRef = c;
    this.props.assignLabelRef(c);
  };

  /**
   * renderAddOn(addOn) {
   * Sub-render method to render Icon/ Addon on textfield.
   */
  renderAddOn(addOn, isAddonAfter = false) {
    const { icon, iconPosition } = this.props;

    const iconStyle = icon
      ? {
          right: iconPosition === 'right' && '0',
          top: '0'
        }
      : null;
    const iconProps = icon
      ? {
          height: '20px',
          width: '20px',
          margin: '8px'
        }
      : null;

    const mergedProps = {
      disabled: addOn.props.disabled || this.props.disabled,
      ...iconProps,
      style: {
        position: 'absolute',
        right: isAddonAfter ? '0' : undefined,
        margin: '8px',
        /* to match textfield background-color like validation or auto complete */
        backgroundColor: 'transparent',
        ...iconStyle,
        ...addOn.props.style
      }
    };

    return React.cloneElement(addOn, mergedProps);
  }

  render() {
    const {
      id,
      disabled,
      errorText,
      warningText,
      validationError,
      theme,
      assignInputRef,
      assignLabelRef,
      label,
      helperText,
      ghostText,
      icon,
      iconPosition,
      // eslint-disable-next-line react/prop-types
      className,
      // eslint-disable-next-line react/prop-types
      style,
      automationId,
      width,
      ...restProps
    } = this.props;

    let { addonBefore, addonAfter } = this.props;

    if (icon) {
      console.warn(
        'Icon prop is deprecated. Please use addonBefore or addonAfter instead.'
      );
      if (iconPosition === 'left') {
        addonBefore = icon;
      } else {
        addonAfter = icon;
      }
    }

    if (!restProps.placeholder && !restProps['aria-label'] && !label) {
      console.warn('Provide one of placeholder, aria-label or label.');
    } else if (!restProps['aria-label'] && !label) {
      console.warn('Make sure the placeholder text is descriptive enough');
    }

    if (restProps.placeholder && ghostText) {
      console.error('Only provide either placeholder or ghostText.');
    }

    const errorWarningText = errorText || warningText;
    const ariaValidationAttributes = getAriaAttributes(
      errorWarningText,
      helperText,
      this.errorWarningTextId,
      this.helperTextId
    );

    const { currentTheme } = getTheme(this.props, this.context);
    const ghostTextElement = ghostText && (
      <GhostTextWrapper>{ghostText}</GhostTextWrapper>
    );

    return (
      <TextFieldWrapper className={className} style={style} width={width}>
        <LabelWrapper
          ref={this.getLabelRef}
          htmlFor={id || this.ariaId}
          disabled={disabled}
          label={label}
        >
          {label}
          <InputWrapper disabled={disabled}>
            {ghostTextElement}
            {addonBefore && this.renderAddOn(addonBefore)}
            <Input
              data-automation-id={automationId}
              {...restProps}
              {...ariaValidationAttributes}
              ref={this.getInputRef}
              id={id || this.ariaId}
              disabled={disabled}
              aria-invalid={!!errorText}
              errorText={errorText}
              warningText={warningText}
              errorWarningText={errorWarningText}
              validationError={validationError}
              theme={currentTheme}
              addonBefore={!!addonBefore}
              addonAfter={!!addonAfter}
              width={width}
            />
            {addonAfter && this.renderAddOn(addonAfter, true)}
          </InputWrapper>
        </LabelWrapper>
        {errorWarningText && (
          <InlineValidationMessage
            id={this.errorWarningTextId}
            message={errorWarningText}
            // eslint-disable-next-line no-nested-ternary
            type={errorText ? 'error' : warningText ? 'warning' : ''}
          >
            {errorWarningText}
          </InlineValidationMessage>
        )}
        {helperText && (
          <HelperTextWrapper id={this.helperTextId} disabled={disabled}>
            {helperText}
          </HelperTextWrapper>
        )}
      </TextFieldWrapper>
    );
  }
}
